import React, { useContext } from "react";
import i18next from "i18next";
import { EADataGridCell } from "../../../components/common/datatable/EADataGrid";
import ROUTES from "../../../config/Routes";
import { Context } from "../../../states/Store";
import MainHeaderCell from "../../../components/common/datatable/MainHeaderCell";
import { PrivilegeEnum } from "services/common/RolesUtils";
import EAButton from '../../../components/common/EAButton';
import EAIcon from '../../../components/common/EAIcon';
import EAColorIcon from '../../../components/common/EAColorIcon';
import { missingColor } from "../../../style/globalStyle";
import UnmanagedBadge from "components/thirdparties/unmanagedThirdParty/UnmanagedBadge";
import EAChipWithBackground from "components/EAChipWithBackground";

const TpSearchChipStyle = {
  cursor: "pointer",
  borderRadius: "30px",
  fontSize: "12px",
  minWidth: "12px",
  padding: "6px 10px",
  minHeight: "10px",
};

const getThirdpartySheetUrl = (accountId, thirdpartyId) =>
  `/${accountId}/${ROUTES.THIRDPARTY_DETAIL.replace(
    ":thirdpartyId",
    thirdpartyId
  )}`;

const TpSearchCell = ({ params, withTooltip = true, allowClick = false, children }) => {
  const [state,] = useContext(Context);

  return (
    <EADataGridCell
      link={
        allowClick &&
        params.row.onboarded &&
        params.row.thirdPartyId &&
        getThirdpartySheetUrl(state.account.id, params.row.thirdPartyId)
      }
      tooltip={withTooltip}
    >
      {children ?? params.value}
    </EADataGridCell>
  );
};

const TpActionCell = ({
                          params,
                          handleResultRowClick,
                          thirdpartyAddLoader,
                          thirdpartyIdLoader,
                          hasAnyOfPrivileges,
                      }) => {
    const isOnboarded = params.row.onboarded;
  
    const canCreateThirdparty = hasAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY
    );

    const isBaseDisabled = !canCreateThirdparty || thirdpartyAddLoader;

    const handleAddClick = () => {

        if (isBaseDisabled) return;

        handleResultRowClick(params.row);
    };

    if (isOnboarded) {
        return (
            <TpSearchCell params={params} withTooltip={false} allowClick={true}>
                <div
                    onClick={() =>
                        !thirdpartyAddLoader &&
                        canCreateThirdparty &&
                        handleResultRowClick(params.row)
                    }
                >
                    <EAIcon icon={"open-eye"} style={{ position: "relative", top: "2px" }} />
                </div>
            </TpSearchCell>
        );
    }

    return (
        <TpSearchCell params={params} withTooltip={false} allowClick={true}>
            <EAButton
                content={
                    <>
                        <span style={{ fontSize: "18px", marginRight: "3px" }}> + </span>{" "}
                        {i18next.t("add")}
                    </>
                }
                customStyle={{
                    ...TpSearchChipStyle,
                }}
                disabled={isBaseDisabled}
                validateLoading={thirdpartyIdLoader === params.row.id}
                onClick={handleAddClick}
            />
        </TpSearchCell>
    );
};



export const tpSearchResultColumns = (
  handleResultRowClick,
  thirdpartyAddLoader,
  thirdpartyIdLoader,
  hasAnyOfPrivileges,
  hasRestrictedDisclosure,
  theme
) => {
  return [
    {
      field: "name",
      headerName: i18next.t("corporateName"),
      flex: 0.2,
      renderHeader: () => (
        <MainHeaderCell name={i18next.t("corporateName")} />
      ),
      renderCell: (params) => <TpSearchCell params={params} >
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: "15px" }}>
          {params.value}
          {params.row.source === 'UNMANAGED' && (
            <UnmanagedBadge />
          )}
        </div>
      </TpSearchCell>,
    },
    {
      field: "identifier",
      headerName: i18next.t("identifier"),
      flex: 0.15,
      renderCell: (params) => <TpSearchCell params={params} />,
    },
    ...(hasRestrictedDisclosure
      ? [
        {
          field: "hasRestrictedDisclosure",
          headerName: i18next.t("hasRestrictedDisclosure.title"),
          flex: 0.15,
          renderCell: (params) => {
            return (
              <TpSearchCell params={params} withTooltip={false}>
                {params.value &&
                  <EAChipWithBackground 
                    label={i18next.t("hasRestrictedDisclosure.title")}
                    color={missingColor}
                    tooltip={i18next.t("hasRestrictedDisclosure.tooltip")}/>
                }
              </TpSearchCell>
            );
          }
        },]
      : []),
    {
      field: "addressStreet",
      headerName: i18next.t("address"),
      flex: 0.15,
      renderCell: (params) => <TpSearchCell params={params} />,
    },
    {
      field: "addressPostCode",
      headerName: i18next.t("postalCode"),
      flex: 0.08,
      renderCell: (params) => <TpSearchCell params={params} />,
    },
    {
      field: "addressCity",
      headerName: i18next.t("city"),
      flex: 0.12,
      renderCell: (params) => <TpSearchCell params={params} />,
    },
    {
      field: "headquarter",
      headerName: i18next.t("thirdpartySearch.businessType"),
      flex: 0.12,
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: [
        { value: true, label: i18next.t("headquarters") },
        { value: false, label: i18next.t("secondaryBranch") },
      ],
      valueFormatter: (params) =>
        i18next.t(params.value ? "headquarters" : "secondaryBranch"),
      renderCell: (params) => {
        params.value = params.value
          ? i18next.t("headquarters")
          : i18next.t("secondaryBranch");
        return <TpSearchCell params={params} />;
      },
    },
    {
      field: "onboarded",
      headerName: i18next.t("thirdpartySearch.thirdpartyState"),
      flex: 0.07,
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: [
        { value: true, label: i18next.t("thirdpartySearch.isOnboarded") },
        { value: false, label: i18next.t("thirdpartySearch.isNotOnboarded") },
      ],
      valueFormatter: (params) =>
        params.value ? i18next.t("thirdpartySearch.isOnboarded") : "",
      renderCell: (params) => {
        return (
          <TpSearchCell params={params} withTooltip={false}>
            {params.value && (
              <div style={{backgroundColor: theme.palette.primary.primary100, borderRadius: "8px", padding: "4px 6px", position: "relative", left: "-8px"}}>
                <EAColorIcon icon={"folowed"} width={20}/>
                <span style={{color: theme.palette.primary.primary800, fontWeight: 600}}>
                  {i18next.t("thirdpartySearch.isOnboarded")}
                </span>
              </div>
            )}
          </TpSearchCell>
        );
      },
    },
    {
      field: "action",
      headerName: i18next.t("actions"),
      flex: 0.1,
      sortable: false,
      disableReorder: true,
      filterable: false,
      disableExport: true,
      hideSortIcons: true,
      disableColumnMenu: true,
      disableMultipleSelection: true,
      disableSelectionOnClick: true,
      isRowSelectable: false,
      hideFooterSelectedRowCount: true,
      align: "center",
      renderCell: (params) => (
          <TpActionCell
              params={params}
              handleResultRowClick={handleResultRowClick}
              thirdpartyAddLoader={thirdpartyAddLoader}
              thirdpartyIdLoader={thirdpartyIdLoader}
              hasAnyOfPrivileges={hasAnyOfPrivileges}
              theme={theme}
          />
      ),
    },
  ];
};
